// src/app/core/models/pre-read.model.ts


export type PreReadStatus = 'Draft' | 'Completed' | 'Submitted';

export interface OpposingParty {
  id?: string;
  name: string;
  phone: string;
  role: string;
  email?: string;
  address?: string;
}

export interface CaseEvent {
  id?: string;
  date: string | null;
  event: string;
  location?: string;
  /** Optional event time (HH:mm, 24-hour — the native <input type="time"> value format). */
  time?: string | null;
}

export interface SupportingDocument {
  id?: string;
  documentType: string;
  documentName: string;
  /** The name last confirmed with the server (on upload success or on
   *  hydrating from a saved draft). Used to detect an in-progress rename
   *  so we only call the document-update API when the name actually
   *  changed, rather than on every save. */
  originalDocumentName?: string;
  file?: File;
  uploadProgress?: number;
  uploaded?: boolean;
 
  uploading?: boolean;
  url?: string;

  uploadError?: string;

  otherCategoryText?: string;
  originalDocumentType?: string;
  originalOtherCategoryText?: string;
}

export interface DeadlineEntry {
  type: 'Filing deadline' | 'Hearing date' | 'Limitation deadline' | 'Notice reply deadline' | 'Other';
  date: string | null;
  notes?: string;
}

export interface PreReadAnswers {
  // Q1
  hasCaseTitle: boolean | null;
  caseTitle: string;

  // Q2
  knowsIssueType: boolean | null;
  issueTypes: string[];
  issueTypeOther: string;

  // Q3
  knowsOpposingParty: boolean | null;
  opposingParties: OpposingParty[];

  // Q4
  incidentDescription: string;

  // Q5
  events: CaseEvent[];

  // Q6 
  hasDocuments: boolean | null;
  documents: SupportingDocument[];

  // Q7
  wantsOutcomeOptions: boolean | null;
  expectedOutcomes: string[];
  expectedOutcomeOther: string;

  // Q8
  legalActionTaken: boolean | null;
  legalActions: string[];
  legalActionOther: string;

  // Q9
  knowsDeadlines: boolean | null;
  deadlines: DeadlineEntry[];

  // Q10
  additionalInformation: string;
}

export interface PreRead {
  pre_read_id: string;
  status: PreReadStatus;
  current_step?: number;
  issue_type?: string[];
  created_at?: string;
  updated_at?: string;
  answers: PreReadAnswers;
}


export interface PreReadSharedLawyer {
  id?: string;
  name?: string;
  email?: string;
}

export interface PreReadListItem {
  pre_read_id: string;
  issue_title: string;
  issue_type: string[];
  status: PreReadStatus;
  updated_at: string;
  shared_with?: PreReadSharedLawyer[] | null;
}

export function hasActiveSharedAccess(entry: Pick<PreReadListItem, 'status' | 'shared_with'> | null | undefined): boolean {
  const isCompleted = (entry?.status || '').toString().trim().toLowerCase() === 'completed';
  const hasSharedLawyers = Array.isArray(entry?.shared_with) && (entry!.shared_with as PreReadSharedLawyer[]).length > 0;
  return isCompleted && hasSharedLawyers;
}

export function displayIssueTitle(entry: Pick<PreReadListItem, 'issue_title'>): string {
  const title = (entry?.issue_title || '').trim();
  return title || 'Untitled Legal Information';
}

// export function displayIssueType(entry: Pick<PreReadListItem, 'issue_type'>): string {
//   const types = entry?.issue_type || [];
//   return types.length ? types.join(', ') : 'Not specified';
// }
export function formatIssueTypeLabel(code: string): string {
  return (code || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/(^|\s)(\p{L})/gu, (_m, space, ch) => space + ch.toUpperCase());
}
export function displayIssueType(entry: Pick<PreReadListItem, 'issue_type'>): string {
  const types = (entry?.issue_type || []).map(formatIssueTypeLabel).filter(Boolean);
  return types.length ? types.join(', ') : 'Not specified';
}

export function emptyPreReadAnswers(): PreReadAnswers {
  return {
    hasCaseTitle: null,
    caseTitle: '',

    knowsIssueType: null,
    issueTypes: [],
    issueTypeOther: '',

    knowsOpposingParty: null,
    opposingParties: [],

    incidentDescription: '',

    events: [],

    hasDocuments: null,
    documents: [],

    wantsOutcomeOptions: null,
    expectedOutcomes: [],
    expectedOutcomeOther: '',

    legalActionTaken: null,
    legalActions: [],
    legalActionOther: '',

    knowsDeadlines: null,
    deadlines: [],

    additionalInformation: ''
  };
}

export function normalizePreReadStatus(status: string): PreReadStatus {
  const s = (status || '').toLowerCase();
  if (s === 'draft') { return 'Draft'; }
  if (s === 'submitted') { return 'Submitted'; }
  return 'Completed';
}


export const PERSONAL_DOC_INDEX = 'citizen_primary';


export interface QuestionAnswerPayload {
  question_code: string;
  value: any;
  other?: string;
  documents?: string[];
}

export const QUESTION_CODES: { [uiStep: number]: string } = {
  1: 'HAS_CASE_TITLE',
  2: 'LEGAL_ISSUE_TYPE',
  3: 'OPPOSITE_PARTY',
  4: 'INCIDENT_DESCRIPTION',
  5: 'IMPORTANT_DATES',
  6: 'SUPPORTING_DOCUMENTS',
  7: 'EXPECTED_OUTCOME',
  8: 'LEGAL_ACTION_TAKEN',
  9: 'URGENT_DEADLINES',
  10: 'ADDITIONAL_INFORMATION'
};


/**
 * Every UI step (1–10, as displayed in the "STEP X OF 10" header) is sent
 * to the backend as its own current_step number. This previously special-
 * cased UI step 6 (Supporting Documents) to send current_step: 7 — but no
 * other step is offset this way (step 4 sends 4, step 9 sends 9, etc.), so
 * that was an isolated off-by-one bug, not an intentional backend
 * numbering convention. Supporting Documents is UI step 6 (see
 * DOCUMENTS_UI_STEP in the questionnaire component and the dialog header,
 * which reads "STEP 6 OF 10") and must save with current_step: 6.
 */
export function toBackendCurrentStep(uiStep: number): number {
  return uiStep;
}


export function toBackendCode(label: string): string {
  return (label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}


function toDateOnly(d: string | null | undefined): string {
  if (!d) { return ''; }
  const already = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (already) { return `${already[1]}-${already[2]}-${already[3]}`; }
  const date = new Date(d);
  if (isNaN(date.getTime())) { return ''; }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}


/**
 * Q5 (IMPORTANT_DATES) payload helper — combines the event's separate
 * `date` ("YYYY-MM-DD") and `time` ("HH:mm", 24-hour — the existing
 * <input type="time"> value shape) UI fields into the single ISO-8601
 * datetime string (fixed +05:30 offset, never converted to `Z`) the
 * `date` field now carries when a time is present.
 * Time stays optional: with no time selected the plain date-only string
 * is preserved exactly as before — no fabricated 00:00:00 is introduced.
 */
function combineDateAndTime(dateStr: string | null | undefined, timeStr: string | null | undefined): string {
  const dateOnly = toDateOnly(dateStr);
  if (!dateOnly) { return ''; }
  const time = (timeStr || '').trim();
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) { return dateOnly; }
  return `${dateOnly}T${match[1]}:${match[2]}:00+05:30`;
}

/**
 * Reverse of combineDateAndTime — decodes a Q5 `date` value back into the
 * UI's separate date/time fields. Handles both the new combined
 * `YYYY-MM-DDTHH:mm:ss+05:30` shape and a plain `YYYY-MM-DD` (no time).
 */
function splitDateAndTime(value: string | null | undefined): { date: string | null; time: string | null } {
  if (!value) { return { date: null, time: null }; }
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (match) { return { date: match[1], time: `${match[2]}:${match[3]}` }; }
  const dateOnly = toDateOnly(value);
  return { date: dateOnly || null, time: null };
}


/**
 * Builds the `value` object for any chip multi-select question that offers
 * an "Other" option with a free-text field (currently Q2/Q7/Q8 — driven
 * generically by whatever list/otherLabel is passed in, never hardcoded to
 * a specific question_code):
 *   - Other not selected:  { selected: [...codes] }
 *   - Other selected:      { selected: [...codes, 'other'], other_text: "..." }
 * `other` stays inside `selected` (never split out into its own array
 * entry), and `other_text` is only ever added when the "Other" chip is
 * actually part of `list`.
 */
function buildMultiSelectValue(
  list: string[],
  otherText: string,
  otherLabel = 'Other'
): { selected: string[]; other_text?: string } {
  const hasOther = (list || []).includes(otherLabel);
  const selected = (list || [])
    .map(toBackendCode)
    .filter(Boolean);
  const value: { selected: string[]; other_text?: string } = { selected };
  if (hasOther) {
    value.other_text = (otherText || '').trim();
  }
  return value;
}

/**
 * Reverse of buildMultiSelectValue: reads a saved answer entry for a chip
 * multi-select + Other question back into { list, otherText, answered }.
 * Accepts both the new `{ selected, other_text }` value shape and the
 * legacy `{ value: [...codes], other: "..." }` shape (top-level `other`
 * sibling of `value`) so previously saved drafts keep hydrating correctly.
 */
function hydrateMultiSelectAnswer(
  uiStep: number,
  entry: any,
  optionLabelForCode: (uiStep: number, code: string) => string | undefined,
  otherLabel = 'Other'
): { list: string[]; otherText: string; answered: boolean } {
  const otherCode = toBackendCode(otherLabel);
  const value = entry?.value;

  const selected: string[] = Array.isArray(value?.selected)
    ? value.selected
    : Array.isArray(value)
      ? value
      : [];
  const legacyOther: string = typeof entry?.other === 'string' ? entry.other : '';
  const hasOther = selected.includes(otherCode) || !!legacyOther;
  const otherText: string = (value?.other_text ?? legacyOther ?? '').trim();

  const list = selected
    .filter(c => c !== otherCode)
    .map(c => optionLabelForCode(uiStep, c) || c);
  if (hasOther) { list.push(otherLabel); }

  return { list, otherText: hasOther ? otherText : '', answered: list.length > 0 || hasOther };
}


export const DEADLINE_KEY_MAP: { [uiLabel: string]: string } = {
  'Filing deadline': 'filing_deadline',
  'Hearing date': 'hearing_date',
  'Limitation deadline': 'limitation_deadline',
  'Notice reply deadline': 'notice_reply_deadline',
  'Other': 'other'
};


export function buildAnswerPayloadForStep(uiStep: number, answers: PreReadAnswers): QuestionAnswerPayload[] {
  const question_code = QUESTION_CODES[uiStep];
  if (!question_code) { return []; }

  switch (uiStep) {
    case 1:
      // HAS_CASE_TITLE — value is an object { has_title, title } (confirmed sample).
      return [{
        question_code,
        value: {
          has_title: answers.hasCaseTitle ? 'yes' : 'no',
          title: answers.hasCaseTitle ? (answers.caseTitle || '').trim() : ''
        }
      }];

    case 2:
      // LEGAL_ISSUE_TYPE — value: { selected: [...codes], other_text?: "..." }
      // (other_text present only when "Other" is selected).
      return [{
        question_code,
        value: answers.knowsIssueType
          ? buildMultiSelectValue(answers.issueTypes, answers.issueTypeOther)
          : { selected: [] }
      }];

    case 3:
      return [{
        question_code,
        value: answers.knowsOpposingParty
          ? (answers.opposingParties || []).map(p => ({
              name: (p.name || '').trim(),
              phone: (p.phone || '').trim(),
              role: (p.role || '').trim(),
              email: (p.email || '').trim(),
              address: (p.address || '').trim()
            }))
          : []
      }];

    case 4:
      return [{ question_code, value: (answers.incidentDescription || '').trim() }];

    case 5:
      // IMPORTANT_DATES — value: [{ date, event, location }]
      // `date` now carries the combined event date+time as a single
      // ISO-8601 string with the +05:30 offset (e.g. 2026-08-25T11:00:00+05:30)
      // when a time was selected, or the plain "YYYY-MM-DD" date when Time
      // was left empty (Time stays optional — see combineDateAndTime).
      // The existing `date` property name/shape is preserved; no separate
      // `time` field is sent.
      return [{
        question_code,
        value: (answers.events || []).map(e => ({
          date: combineDateAndTime(e.date, e.time),
          event: (e.event || '').trim(),
          location: (e.location || '').trim()
        }))
      }];

    case 7:
      // EXPECTED_OUTCOME — value: { selected: [...codes], other_text?: "..." }
      // (other_text present only when "Other" is selected).
      return [{
        question_code,
        value: answers.wantsOutcomeOptions
          ? buildMultiSelectValue(answers.expectedOutcomes, answers.expectedOutcomeOther)
          : { selected: [] }
      }];

    case 8:
      // LEGAL_ACTION_TAKEN — value: { selected: [...codes], other_text?: "..." }
      // (other_text present only when "Other" is selected).
      return [{
        question_code,
        value: answers.legalActionTaken
          ? buildMultiSelectValue(answers.legalActions, answers.legalActionOther)
          : { selected: [] }
      }];

    case 9: {
      // URGENT_DEADLINES — each deadline type's date key gets a matching
      // "<key>_notes" sibling so the Notes entered per deadline type are
      // included in the payload alongside its existing date field.
      const deadlineValue: { [k: string]: string } = {
        has_deadlines: answers.knowsDeadlines ? 'yes' : 'no',
        filing_deadline: '',
        hearing_date: '',
        limitation_deadline: '',
        notice_reply_deadline: '',
        other: '',
        filing_deadline_notes: '',
        hearing_date_notes: '',
        limitation_deadline_notes: '',
        notice_reply_deadline_notes: '',
        other_notes: ''
      };
      if (answers.knowsDeadlines) {
        (answers.deadlines || []).forEach(d => {
          const key = DEADLINE_KEY_MAP[d.type];
          if (key) {
            deadlineValue[key] = toDateOnly(d.date);
            deadlineValue[`${key}_notes`] = (d.notes || '').trim();
          }
        });
      }
      return [{ question_code, value: deadlineValue }];
    }

    case 10:
      return [{ question_code, value: (answers.additionalInformation || '').trim() }];

    default:
      return [];
  }
}


export const DOCUMENT_USER_TAG_MAP: { [uiLabel: string]: string } = {
  'Agreements': 'agreements',
  'Audio/Video': 'audio_video',
  'Contracts': 'contracts',
  'Court orders': 'court_orders',
  'Emails': 'emails',
  'FIRs': 'firs',
  'Legal notices': 'legal_notices',
  'Other evidence': 'other_evidence',
  'Photographs': 'photographs',
  'Other': 'other'
};

export const SUPPORTING_DOC_CATEGORY_MAP: { [uiLabel: string]: string } = {
  'Agreements': 'agreement',
  'Audio/Video': 'audio_video',
  'Contracts': 'contract',
  'Court orders': 'court_order',
  'Emails': 'email',
  'FIRs': 'fir',
  'Legal notices': 'legal_notice',
  'Other evidence': 'other_evidence',
  'Photographs': 'photograph',
  'Other': 'other'
};

export const REVERSE_SUPPORTING_DOC_CATEGORY_MAP: { [code: string]: string } =
  Object.keys(SUPPORTING_DOC_CATEGORY_MAP).reduce((acc, label) => {
    acc[SUPPORTING_DOC_CATEGORY_MAP[label]] = label;
    return acc;
  }, {} as { [code: string]: string });

/** Reverse of DOCUMENT_USER_TAG_MAP — maps a document's own `user_tags`
 *  code (the pluralized form used on the per-document upload/rename APIs,
 *  e.g. "firs", "agreements") back to its UI label. Kept separate from
 *  REVERSE_SUPPORTING_DOC_CATEGORY_MAP (which reverses the singular
 *  question-level `categories` codes, e.g. "fir", "agreement") since the
 *  two backend surfaces use different code shapes for the same UI label. */
export const REVERSE_DOCUMENT_USER_TAG_MAP: { [code: string]: string } =
  Object.keys(DOCUMENT_USER_TAG_MAP).reduce((acc, label) => {
    acc[DOCUMENT_USER_TAG_MAP[label]] = label;
    return acc;
  }, {} as { [code: string]: string });

/**
 * Resolves a single document's own raw tag/category value (as returned by
 * the backend on that specific document entry) back to its UI Document
 * Type label. Tries the document-level user_tags code shape first, then
 * the question-level category code shape, since either may appear
 * depending on which backend field populated the document entry. Falls
 * back to 'Other' (never to a specific type like 'Agreements') when the
 * tag is missing or unrecognized, so an unmapped value never masquerades
 * as a real category.
 */
function documentTypeLabelForTag(tag: string | undefined | null): string {
  const t = (tag || '').trim().toLowerCase();
  if (!t || t === 'other') { return 'Other'; }
  return REVERSE_DOCUMENT_USER_TAG_MAP[t] || REVERSE_SUPPORTING_DOC_CATEGORY_MAP[t] || 'Other';
}


export function hydratePreReadAnswers(
  apiAnswers: any,
  optionLabelForCode: (uiStep: number, code: string) => string | undefined
): PreReadAnswers {
  const result = emptyPreReadAnswers();

  if (!Array.isArray(apiAnswers)) {
   
    return apiAnswers && typeof apiAnswers === 'object' ? { ...result, ...apiAnswers } : result;
  }

  const findEntry = (code: string): any =>
    apiAnswers.find((a: any) => a && a.question_code === code);

  // Q1 — HAS_CASE_TITLE
  const q1 = findEntry(QUESTION_CODES[1]);
  if (q1 !== undefined) {
    result.hasCaseTitle = q1.value?.has_title === 'yes';
    result.caseTitle = q1.value?.title ?? '';
  }

  // Q2 — LEGAL_ISSUE_TYPE
  const q2 = findEntry(QUESTION_CODES[2]);
  if (q2 !== undefined) {
    const { list, otherText, answered } = hydrateMultiSelectAnswer(2, q2, optionLabelForCode);
    result.issueTypes = list;
    result.issueTypeOther = otherText;
    result.knowsIssueType = answered;
  }

  // Q3 — OPPOSITE_PARTY
  const q3 = findEntry(QUESTION_CODES[3]);
  if (q3 !== undefined) {
    const parties: any[] = Array.isArray(q3.value) ? q3.value : [];
    result.opposingParties = parties.map(p => ({
      name: p?.name ?? '',
      phone: p?.phone ?? '',
      role: p?.role ?? '',
      email: p?.email ?? '',
      address: p?.address ?? ''
    }));
    result.knowsOpposingParty = parties.length > 0;
  }

  // Q4 — INCIDENT_DESCRIPTION
  const q4 = findEntry(QUESTION_CODES[4]);
  if (q4 !== undefined) {
    result.incidentDescription = typeof q4.value === 'string' ? q4.value : '';
  }

  // Q5 — IMPORTANT_DATES
  const q5 = findEntry(QUESTION_CODES[5]);
  if (q5 !== undefined) {
    const events: any[] = Array.isArray(q5.value) ? q5.value : [];
    result.events = events.map(e => {
      // Decode the combined date+time back into the UI's separate fields.
      // Falls back to a legacy separate `time` property (pre-existing
      // drafts saved before Q5 date+time were combined) when the `date`
      // value itself carries no time component.
      const { date, time } = splitDateAndTime(e?.date);
      return {
        date,
        event: e?.event ?? '',
        location: e?.location ?? '',
        time: time ?? (e?.time ?? null)
      };
    });
  }

  // Q6 — SUPPORTING_DOCUMENTS. `value.categories` is the aggregate SET of
  // categories across every uploaded document (used only to rebuild the
  // request payload — see buildSupportingDocumentsAnswer in the
  // questionnaire component) and must never be re-applied as a single
  // type to every document below. Each document's own type/tag lives on
  // that document's own entry (`user_tags` / `category` / `document_type`,
  // depending on which backend field populated it) and is read
  // individually per entry so Agreements/FIRs/etc. stay distinct per
  // document across a revisit.
  // const q6 = findEntry(QUESTION_CODES[6]);
  // if (q6 !== undefined) {
  //   const hasDocuments = q6.value?.has_documents === true || q6.value?.has_documents === 'yes';
  //   result.hasDocuments = hasDocuments;
  //   // `other_text` is the current field name; `other` is kept as a
  //   // fallback so previously saved drafts (written before this field was
  //   // renamed) still hydrate correctly.
  //   const otherText: string = q6.value?.other_text ?? q6.value?.other ?? '';
  //   const documentEntries: any[] = Array.isArray(q6.documents) ? q6.documents : [];
  //   result.documents = hasDocuments
  //     ? documentEntries.map((entry, i) => {
  //         const isObj = entry && typeof entry === 'object';
  //         const id = isObj ? (entry.document_id ?? entry.id ?? '') : entry;
  //         const name = isObj ? (entry.document_name ?? entry.name ?? `Document ${i + 1}`) : `Document ${i + 1}`;
  //         const ownTag: string | undefined = isObj
  //           ? (Array.isArray(entry.user_tags) && entry.user_tags.length ? entry.user_tags[0]
  //               : Array.isArray(entry.tags) && entry.tags.length ? entry.tags[0]
  //               : entry.category ?? entry.document_type ?? undefined)
  //           : undefined;
  //         const documentType = documentTypeLabelForTag(ownTag);
  //         return {
  //           id,
  //           documentType,
  //           documentName: name,
  //           originalDocumentName: name,
  //           uploaded: true,
  //           otherCategoryText: documentType === 'Other' ? otherText : ''
  //         };
  //       })
  //     : [];
  // }
  const q6 = findEntry(QUESTION_CODES[6]);
if (q6 !== undefined) {
  const hasDocuments = q6.value?.has_documents === true || q6.value?.has_documents === 'yes';
  result.hasDocuments = hasDocuments;

  // Per-document id -> {category, other_text} map — the authoritative
  // source now that type is saved per document (see
  // buildSupportingDocumentsAnswer) instead of as one aggregate set.
  const perDocMap = new Map<string, { category?: string; other_text?: string }>();
  const docCategoryEntries: any[] = Array.isArray(q6.value?.document_categories) ? q6.value.document_categories : [];
  docCategoryEntries.forEach((entry: any) => {
    if (entry?.id) { perDocMap.set(entry.id, entry); }
  });

  const legacyOtherText: string = q6.value?.other_text ?? q6.value?.other ?? '';
  const documentEntries: any[] = Array.isArray(q6.documents) ? q6.documents : [];

  result.documents = hasDocuments
    ? documentEntries.map((entry, i) => {
        const isObj = entry && typeof entry === 'object';
        const id = isObj ? (entry.document_id ?? entry.id ?? '') : entry;
        const name = isObj ? (entry.document_name ?? entry.name ?? `Document ${i + 1}`) : `Document ${i + 1}`;

        // Prefer the new per-document map; fall back to whatever the
        // document's own entry carries (covers older saved drafts).
        const mapped = perDocMap.get(id);
        const ownTag: string | undefined = mapped?.category
          ?? (isObj
              ? (Array.isArray(entry.user_tags) && entry.user_tags.length ? entry.user_tags[0]
                  : Array.isArray(entry.tags) && entry.tags.length ? entry.tags[0]
                  : entry.category ?? entry.document_type ?? undefined)
              : undefined);
        const documentType = documentTypeLabelForTag(ownTag);
        const otherCategoryText = documentType === 'Other'
          ? (mapped?.other_text ?? legacyOtherText)
          : '';

        return {
          id,
          documentType,
          documentName: name,
          originalDocumentName: name,
          uploaded: true,
          otherCategoryText,
          originalDocumentType: documentType,
          originalOtherCategoryText: otherCategoryText, 
        };
      })
    : [];
}

  // Q7 — EXPECTED_OUTCOME
  const q7 = findEntry(QUESTION_CODES[7]);
  if (q7 !== undefined) {
    const { list, otherText, answered } = hydrateMultiSelectAnswer(7, q7, optionLabelForCode);
    result.expectedOutcomes = list;
    result.expectedOutcomeOther = otherText;
    result.wantsOutcomeOptions = answered;
  }

  // Q8 — LEGAL_ACTION_TAKEN
  const q8 = findEntry(QUESTION_CODES[8]);
  if (q8 !== undefined) {
    const { list, otherText, answered } = hydrateMultiSelectAnswer(8, q8, optionLabelForCode);
    result.legalActions = list;
    result.legalActionOther = otherText;
    result.legalActionTaken = answered;
  }

  // Q9 — URGENT_DEADLINES. 
  const q9 = findEntry(QUESTION_CODES[9]);
  if (q9 !== undefined) {
    result.knowsDeadlines = q9.value?.has_deadlines === 'yes';
    const deadlines: DeadlineEntry[] = [];
    Object.keys(DEADLINE_KEY_MAP).forEach(uiLabel => {
      const backendKey = DEADLINE_KEY_MAP[uiLabel];
      const dateVal = q9.value?.[backendKey];
      if (dateVal !== undefined && dateVal !== null && dateVal !== '') {
        const notesVal = q9.value?.[`${backendKey}_notes`];
        deadlines.push({
          type: uiLabel as DeadlineEntry['type'],
          date: dateVal,
          notes: typeof notesVal === 'string' ? notesVal : ''
        });
      }
    });
    result.deadlines = deadlines;
  }

  // Q10 — ADDITIONAL_INFORMATION
  const q10 = findEntry(QUESTION_CODES[10]);
  if (q10 !== undefined) {
    result.additionalInformation = typeof q10.value === 'string' ? q10.value : '';
  }

  return result;
}